/**
 * Zadeva Store - Case management with conversations, dossier, and documents
 * Uses Zustand + persist middleware (localStorage)
 */
import { create } from 'zustand';
import { useDocumentViewerStore } from '@/lib/stores/document-viewer-store';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type { DossierCard } from './dossier-store';
import type { SearchResultCard, CoverageData, CitationEntry } from '@/lib/types/search-results';
import type { SourceFilter, PeriodFilter, CourtFilter } from './filter-store';
import type { ClaimsResponse } from '@/lib/citations/claims';
import type { ChatModelId } from '@/lib/ai/chat-models';
import {
  remoteCreateZadeva, remoteDeleteZadeva, remotePatchZadeva,
  remotePushMessage, remoteDeleteMessage, remoteClearMessages, remotePutCanvas, remoteDeleteCanvas,
} from '@/lib/stores/sync';

// ---- Types ----

export type FeedbackRating = 'up' | 'down';

export type FeedbackReason =
  | 'incorrect'
  | 'not_useful'
  | 'incomplete'
  | 'wrong_source'
  | 'too_long'
  | 'too_short'
  | 'other';

export interface MessageFeedbackData {
  rating: FeedbackRating;
  reason?: FeedbackReason;
  customText?: string;
  timestamp: number;
}

export type RubricScore = 1 | 2 | 3 | 4 | 5;

export interface EvalRubric {
  /** Pravna pravilnost vsebine */
  natancnost?: RubricScore;
  /** Organiziranost, hierarhija, naslovi */
  struktura?: RubricScore;
  /** Pokritost vprašanja, popolnost */
  vsebina?: RubricScore;
  /** Berljivost, terminologija */
  jasnost?: RubricScore;
  /** Pravilnost REF-ov, citatov, pravnih sklicev */
  citiranje?: RubricScore;
  /** Logična povezanost, sklepanje */
  argumentacija?: RubricScore;
  /** Prosta opomba (multi-line, brez limita) */
  opomba?: string;
  annotatedAt: number;
}

export interface MessageEvalData {
  /** Označeno kot relevantno za eval pipeline (lahko brez ocenjevanja) */
  markedForEval?: boolean;
  /** Strukturirana rubrika (1-5 po 6 dimenzijah + opomba) */
  rubric?: EvalRubric;
}

export interface ActionPlanData {
  steps: Array<{
    id: string;
    source: string;
    query: string;
    label: string;
    enabled: boolean;
  }>;
  status: 'pending' | 'confirmed' | 'executing' | 'completed' | 'skipped';
  executingStepIndex?: number;
  completedSteps?: number[];
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Stabilen per-zadeva indeks za server sinhronizacijo (seja 219, A4). Monotono naraščajoč; regeneracija ponovno uporabi isti ordinal → upsert prepiše. */
  ordinal?: number;
  status?: 'sending' | 'sent' | 'error';
  stopped?: boolean; // Stream was manually stopped by user
  plan?: ActionPlanData; // Action plan for comprehensive searches
  sources?: Array<{ type: 'pisrs' | 'eurlex' | 'sodnapraksa' | 'usrs' | 'unknown'; label: string; url?: string }>;
  lawCards?: string[];
  resultCards?: SearchResultCard[];
  coverageData?: CoverageData;
  queryNormalization?: { userQuery: string; resolvedTitle: string };
  citationMap?: CitationEntry[];
  claims?: ClaimsResponse; // Structured claims from Phase B extraction
  followUps?: string[];
  /** Kontekst okrnjen zaradi velikosti (seja 129, P0-B). Prikaz: opozorilni banner. */
  contextTrimmed?: { droppedSections: string[] };
  feedback?: MessageFeedbackData;
  /** Eval pipeline metadata (mark + rubric ocene) — seja 155 */
  eval?: MessageEvalData;
  /** SP-LAZY (seja 155): dodatne sodnapraksa karte iz "Pokaži več" gumba */
  additionalSpCards?: SearchResultCard[];
  /** SP-LAZY: lastQuery za lazy-load (search query, uporabljen v sodnapraksa intentu) */
  spLazyQuery?: string;
  /** SP-LAZY: trenutna stran (1 = glavni odgovor, 2+ = dodatne karte) */
  spLazyPage?: number;
  /** SP-LAZY: ali obstaja še več sodb iz iste poizvedbe */
  spHasMore?: boolean;
  /** LLM model, ki je generiral ta odgovor (provenienca za badge) — GaMS migracija. */
  model?: ChatModelId;
  /** Iskalni laboratorij (seja 384): nastavitev iskanja po členih, s katero je nastal odgovor. */
  searchNote?: string;
}

export interface ZadevaDocument {
  id: string;
  name: string;
  uploadedAt: number;
  textLength: number;
}

export type CanvasDocFormat = 'mnenje' | 'pojasnilo' | 'komentar' | 'analiza' | 'prosto';

export interface CanvasDocMetadata {
  predlagatelj?: string;       // "Ministrstvo za okolje"
  datum?: string;              // "2026-03-04"
  naslovPredpisa?: string;     // "Zakon o varstvu okolja (ZVO-2)"
  evStevilka?: string;         // "EVA 2025-2611-0042"
  oporneTocke?: string[];      // Bullet points za AI expand
}

export interface CanvasTurn {
  role: 'user' | 'ai';
  timestamp: number;
  addedContent?: string;
}

export interface CanvasDoc {
  id: string;
  title: string;
  format: CanvasDocFormat;
  html: string;           // TipTap HTML output
  metadata?: CanvasDocMetadata;
  turns?: CanvasTurn[];   // Ping-pong history (max 20)
  createdAt: number;
  updatedAt: number;
}

export interface SavedFilters {
  sources: SourceFilter[];
  period: PeriodFilter;
  courts: CourtFilter[];
  yearFrom?: number | null;
  yearTo?: number | null;
}

/** Status nomotehničnega pregleda (seja 177 — persistence + reconnect) */
export type NomotehniskaReviewStatus = 'running' | 'complete' | 'error' | 'aborted';

/** Faza pregleda — preživetje napredka skozi reload */
export interface NomotehniskaPhaseSnapshot {
  phase: number | 'S';
  name: string;
  status: 'pending' | 'running' | 'complete' | 'error' | 'skipped';
  details?: string;
  issueCount?: number;
  count?: number;
  warnings?: string[];
  message?: string;
}

/** Povzetek pregleda (za sticky indicator + sidebar kartico) */
export interface NomotehniskaReviewSummary {
  totalIssues: number;
  critical: number;
  major: number;
  minor: number;
  overallAssessment: string;
}

/** Rezultat nomotehničnega pregleda — per-zadeva, za follow-up vprašanja + reconnect */
export interface NomotehniskaReviewResult {
  documentName: string;
  analysisText: string;
  timestamp: number;
  /** Seja 177: server job id za poll/reconnect prek /api/nomotehnika-v2/status?id=... */
  reviewId?: string;
  /** Seja 177: kaj backend trenutno počne. Default 'complete' za starejše vnose. */
  status?: NomotehniskaReviewStatus;
  /** Seja 177: zadnji znan napredek faz */
  phases?: NomotehniskaPhaseSnapshot[];
  /** Seja 177: povzetek (samo če status==='complete') */
  summary?: NomotehniskaReviewSummary;
  /** Seja 177: za hash-diff detekcijo "isti dokument že pregledan" */
  documentTextHash?: string;
  /** Seja 177: timestamp začetka (za elapsed display po reconnect-u) */
  startedAt?: number;
  /** Seja 177: error message če status==='error' */
  errorMessage?: string;
  /** Seja 177: strukturirano poročilo (SynthesisReport) — za "Generiraj mnenje" gumb po dokončanem pregledu.
   *  Tipiziran lazy kot import-only (preprečuje circular dep med store-i in nomotehnika modulom). */
  report?: import('@/lib/nomotehnika/types').SynthesisReport;
  /** Seja 177: generirano mnenje (opinion text) iz synthesisOnly fast path */
  opinionText?: string;
  /** Seja 177: format mnenja (per-article ali flowing-prose) */
  opinionFormat?: 'per-article' | 'flowing-prose';
  /** Seja 177: timestamp generiranja mnenja */
  opinionGeneratedAt?: number;
}

/** Kontekst zadnjega iskanja — per-zadeva, za follow-up vprašanja */
export interface SearchContext {
  lastPisrs?: { pisrsId: string; abbr: string; title: string };
  lastEurLex?: { celex: string; title: string };
  lastUsrs?: { caseNumber: string; title: string };
  lastSodnaPraksa?: { query: string; opravilnaSt?: string };
  lastCjeu?: { celex: string; caseNumber?: string };
  lastSearchType?: string;  // 'pisrs' | 'eurlex' | 'comprehensive' | etc.
  lastQuery?: string;
  timestamp?: number;
}

export interface Zadeva {
  id: string;
  title: string;
  status: 'odprta' | 'zaprta' | 'v-delu';
  createdAt: number;
  updatedAt: number;
  messages: Message[];
  dossierCards: DossierCard[];
  documents: ZadevaDocument[];
  canvasDocs: CanvasDoc[];
  savedFilters?: SavedFilters;
  searchContext?: SearchContext;
  latestNomotehniskaReview?: NomotehniskaReviewResult;
  /** Rolling destilat pogovora (seja 219, B1): povzetek starejšega dela pogovora */
  conversationSummary?: string;
  /** Koliko vodilnih sporočil pokriva conversationSummary (server ga pošlje prek META framea) */
  summarizedUpToIndex?: number;
}

interface ZadevaState {
  zadeve: Zadeva[];
  activeZadevaId: string | null;
  leftPanelTab: 'zadeve' | 'dosje';
  _migrated: boolean;

  // Global canvas docs (accessible from any zadeva)
  canvasDocs: CanvasDoc[];

  // Zadeva CRUD
  createZadeva: (title?: string) => string;
  deleteZadeva: (id: string) => void;
  clearAllZadeve: () => void;
  setActiveZadeva: (id: string) => void;
  updateZadevaTitle: (id: string, title: string) => void;
  updateZadevaStatus: (id: string, status: Zadeva['status']) => void;

  // Panel
  setLeftPanelTab: (tab: 'zadeve' | 'dosje') => void;

  // Messages
  addMessage: (msg: Message) => void;
  updateMessage: (msgId: string, partial: Partial<Message>) => void;
  getActiveMessages: () => Message[];
  clearActiveMessages: () => void;
  removeLastAssistantMessage: () => string | null;

  // Dossier (per-zadeva)
  addDossierCard: (card: Omit<DossierCard, 'id' | 'ts'>) => void;
  removeDossierCard: (cardId: string) => void;
  clearDossier: () => void;
  getActiveDossierCards: () => DossierCard[];

  // Documents
  addDocument: (doc: Omit<ZadevaDocument, 'id'>) => void;
  removeDocument: (docId: string) => void;

  // Canvas Docs (global)
  createCanvasDoc: (format: CanvasDocFormat, metadata?: CanvasDocMetadata) => string | null;
  updateCanvasDoc: (id: string, html: string, title?: string) => void;
  deleteCanvasDoc: (id: string) => void;
  clearAllCanvasDocs: () => void;
  getCanvasDoc: (id: string) => CanvasDoc | undefined;
  getAllCanvasDocs: () => CanvasDoc[];
  addCanvasTurn: (docId: string, turn: CanvasTurn) => void;
  addFragmentToCanvas: (docId: string, html: string, sourceType: string, sourceLabel: string) => void;

  // Filters (per-zadeva persistence)
  saveFilters: (filters: SavedFilters, zadevaId?: string) => void;
  getSavedFilters: () => SavedFilters | undefined;

  // Search context (per-zadeva)
  updateSearchContext: (partial: Partial<SearchContext>) => void;
  getSearchContext: () => SearchContext | undefined;

  // Rolling destilat pogovora (seja 219, B1)
  setConversationSummary: (summary: string, upToIndex: number) => void;
  getConversationSummary: () => { summary: string; upToIndex: number } | undefined;

  // Feedback
  setMessageFeedback: (msgId: string, feedback: MessageFeedbackData | undefined) => void;

  // Eval (seja 155)
  setMessageEval: (msgId: string, evalData: MessageEvalData | undefined) => void;

  // SP-LAZY (seja 155)
  appendSpCards: (msgId: string, newCards: SearchResultCard[], nextPage: number, hasMore: boolean) => void;

  // Nomotehnika follow-up
  saveNomotehniskaReview: (review: NomotehniskaReviewResult) => void;
  /** Seja 177: partial merge brez prepisovanja celotnega review-ja (za sproti shranjevanje med tek-om) */
  updateNomotehniskaReview: (patch: Partial<NomotehniskaReviewResult>) => void;
  /** Seja 177: izbriši zadnji pregled (npr. ko user klikne "Zavrzi") */
  clearNomotehniskaReview: () => void;
  getLatestNomotehniskaReview: () => NomotehniskaReviewResult | undefined;

  // Migration
  setMigrated: () => void;

  // Server sync hidracija (seja 219, A4) — interni setterji; NE sprožajo push (izogib echo)
  // createdAt/updatedAt sprejmeta tudi ISO string — starejši odgovori (in localStorage
  // vnosi, hidrirani z njimi) so imeli Date serializiran v string; `toEpochMs` normalizira.
  _applyServerZadeveList: (list: Array<{ id: string; title: string; status: string; createdAt: number | string; updatedAt: number | string }>) => void;
  _applyServerZadevaDetail: (id: string, detail: Record<string, unknown>) => void;
  _applyServerCanvas: (docs: CanvasDoc[]) => void;
}

// ---- Constants ----

const MAX_ZADEVE = 30;
const MAX_MESSAGES = 200;
const MAX_DOSSIER_CARDS = 50;
const MAX_CANVAS_DOCS = 50; // Global limit (was 20 per-zadeva)
const MAX_CANVAS_DOC_SIZE = 100 * 1024; // 100KB

// Stable empty array references to prevent React SSR infinite loop
// (getServerSnapshot must return cached values)
const EMPTY_MESSAGES: Message[] = [];
const EMPTY_DOSSIER_CARDS: DossierCard[] = [];
const EMPTY_DOCUMENTS: ZadevaDocument[] = [];
const EMPTY_ZADEVE: Zadeva[] = [];

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
}

/**
 * Normalizira časovno oznako v epoch ms. Server vrača števila, a ISO string (Prisma
 * Date prek JSON) se je že znašel v localStorage — z njim `b.updatedAt - a.updatedAt`
 * vrne NaN, sort ne razvrsti nič in vrstni red zadev je odvisen od lokalnega cachea
 * (različna zgodovina na različnih napravah). Vrne 0 za neuporabno vrednost.
 */
export function toEpochMs(v: number | string | null | undefined): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const parsed = Date.parse(v);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

// ---- Quota-safe storage ----
//
// canvasDocs (dokumenti, ki jih pravnik piše) živijo v ISTEM localStorage ključu
// kot celotna zgodovina pogovorov. Vsako AI sporočilo nosi težke metapodatke
// (resultCards, citationMap, claims, coverageData) ~20-30 KB. Po dovolj
// pogovorih seštevek preseže ~5 MB localStorage limit → setItem vrže
// QuotaExceededError → privzeti persist write TIHO PADE → canvas, ustvarjen
// v tej seji, se nikoli ne shrani in po reloadu izgine ("se ne da priklicati").
//
// Ta ovojnica ob kvoti postopno obreže težka, neesencialna polja iz STAREJŠIH
// sporočil in ponovno poskusi. canvasDocs se NIKOLI ne obreže — pravnikov
// izdelek ima absolutno prednost pred metapodatki kartic v stari zgodovini.

/** Težka, ob-reload neesencialna polja sporočila (kartice se lahko izgubijo, AI tekst NE). */
const HEAVY_MESSAGE_FIELDS = [
  'resultCards',
  'additionalSpCards',
  'citationMap',
  'claims',
  'coverageData',
] as const;

function isQuotaExceededError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    // Različni brskalniki: standard ime/koda, Firefox legacy
    (e.name === 'QuotaExceededError' ||
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      e.code === 22 ||
      e.code === 1014)
  );
}

type PersistEnvelope = {
  state?: { zadeve?: Array<Record<string, unknown>>; activeZadevaId?: string | null };
  version?: number;
};

/**
 * Obreže persistirani state na dani agresivnosti (kumulativno: raven N vključuje 1..N).
 *  - raven 1: težka polja iz vseh razen zadnjih 4 sporočil vsake zadeve
 *  - raven 2: težka polja iz VSEH sporočil
 *  - raven 3: obdrži samo aktivno + 4 najnovejše zadeve (po updatedAt)
 * canvasDocs ostane nedotaknjen na vseh ravneh.
 */
function pruneEnvelope(env: PersistEnvelope, level: number): PersistEnvelope {
  const state = env.state;
  if (!state || !Array.isArray(state.zadeve)) return env;

  if (level >= 1) {
    const keepRecent = level >= 2 ? 0 : 4;
    state.zadeve = state.zadeve.map((z) => {
      const messages = z.messages as Array<Record<string, unknown>> | undefined;
      if (!Array.isArray(messages)) return z;
      const cut = Math.max(0, messages.length - keepRecent);
      const trimmed = messages.map((m, i) => {
        if (i >= cut) return m;
        const copy = { ...m };
        for (const f of HEAVY_MESSAGE_FIELDS) delete copy[f];
        return copy;
      });
      return { ...z, messages: trimmed };
    });
  }

  if (level >= 3) {
    const activeId = state.activeZadevaId;
    const sorted = [...state.zadeve].sort(
      // toEpochMs: brez normalizacije bi ISO string dal NaN → ta raven bi ohranila
      // naključnih 5 zadev namesto najnovejših (in ostale nepovratno vrgla iz cachea).
      (a, b) => toEpochMs(b.updatedAt as number | string) - toEpochMs(a.updatedAt as number | string)
    );
    const keepIds = new Set(sorted.slice(0, 5).map((z) => z.id as string));
    if (activeId) keepIds.add(activeId);
    state.zadeve = state.zadeve.filter((z) => keepIds.has(z.id as string));
  }

  return env;
}

const quotaSafeStorage: StateStorage = {
  getItem: (name) =>
    typeof localStorage !== 'undefined' ? localStorage.getItem(name) : null,
  removeItem: (name) => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(name);
  },
  setItem: (name, value) => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(name, value);
      return;
    } catch (e) {
      if (!isQuotaExceededError(e)) {
        console.warn('[zadeve-store] localStorage.setItem napaka (ne-kvota):', e);
        return;
      }
    }
    // Kvota presežena — postopno obrezuj in ponavljaj (canvas vedno ohranjen).
    for (let level = 1; level <= 3; level++) {
      try {
        const env = JSON.parse(value) as PersistEnvelope;
        const pruned = pruneEnvelope(env, level);
        localStorage.setItem(name, JSON.stringify(pruned));
        console.warn(
          `[zadeve-store] localStorage kvota presežena — zgodovina obrezana (raven ${level}); canvas ohranjen`
        );
        return;
      } catch (e2) {
        if (!isQuotaExceededError(e2)) {
          console.warn('[zadeve-store] napaka pri obrezovanju za kvoto:', e2);
          return;
        }
      }
    }
    console.error(
      '[zadeve-store] localStorage kvota presežena tudi po obrezovanju — shranjevanje preskočeno'
    );
  },
};

// ---- Store ----

/**
 * Push posodobljenega sporočila na server (A4). Bere trenutno stanje sporočila po
 * id-ju in ga pošlje prek ordinala. Uporaba: vse akcije, ki spremenijo eno
 * sporočilo (updateMessage, appendSpCards, setMessageFeedback, setMessageEval).
 */
function pushMessageById(
  getState: () => { activeZadevaId: string | null; zadeve: Zadeva[] },
  msgId: string
): void {
  const s = getState();
  const zad = s.zadeve.find((z) => z.id === s.activeZadevaId);
  const m = zad?.messages.find((mm) => mm.id === msgId);
  if (s.activeZadevaId && m && m.ordinal != null) {
    remotePushMessage(s.activeZadevaId, m.ordinal, m);
  }
}

/** Push canvas dokumenta na server (A4). Bere trenutno stanje po id-ju in PUT. */
function pushCanvasById(getState: () => { canvasDocs: CanvasDoc[] }, id: string): void {
  const doc = getState().canvasDocs.find((d) => d.id === id);
  if (doc) {
    remotePutCanvas({ id: doc.id, format: doc.format, title: doc.title, html: doc.html, metadata: doc.metadata, turns: doc.turns });
  }
}

export const useZadevaStore = create<ZadevaState>()(
  persist(
    (set, get) => ({
      zadeve: [],
      activeZadevaId: null,
      leftPanelTab: 'zadeve',
      _migrated: false,
      canvasDocs: [],

      // ---- Zadeva CRUD ----

      createZadeva: (title?: string) => {
        const id = generateId();
        const now = Date.now();
        const newZadeva: Zadeva = {
          id,
          title: title || 'Nov pogovor',
          status: 'odprta',
          createdAt: now,
          updatedAt: now,
          messages: [],
          dossierCards: [],
          documents: [],
          canvasDocs: [],
        };

        set((state) => {
          let zadeve = [newZadeva, ...state.zadeve];
          if (zadeve.length > MAX_ZADEVE) {
            zadeve = zadeve.slice(0, MAX_ZADEVE);
          }
          return { zadeve, activeZadevaId: id };
        });

        remoteCreateZadeva({ id, title: newZadeva.title, status: newZadeva.status });
        return id;
      },

      deleteZadeva: (id) => {
        set((state) => {
          const zadeve = state.zadeve.filter((z) => z.id !== id);
          const activeZadevaId =
            state.activeZadevaId === id
              ? zadeve[0]?.id ?? null
              : state.activeZadevaId;
          return { zadeve, activeZadevaId };
        });
        remoteDeleteZadeva(id);
      },

      clearAllZadeve: () => {
        const ids = get().zadeve.map((z) => z.id);
        set({ zadeve: [], activeZadevaId: null });
        // Strežnik kaskadno počisti sporočila/dokumente/dosje vsake zadeve.
        for (const id of ids) remoteDeleteZadeva(id);
      },

      setActiveZadeva: (id) => {
        set({ activeZadevaId: id });
        // Lazy naloži polno vsebino zadeve iz baze ob prvem odprtju (A4).
        void import('@/lib/stores/sync').then((m) => m.loadZadevaDetail(id));
      },

      updateZadevaTitle: (id, title) => {
        const trimmed = title.slice(0, 100);
        set((state) => ({
          zadeve: state.zadeve.map((z) =>
            z.id === id ? { ...z, title: trimmed, updatedAt: Date.now() } : z
          ),
        }));
        remotePatchZadeva(id, { title: trimmed });
      },

      updateZadevaStatus: (id, status) => {
        set((state) => ({
          zadeve: state.zadeve.map((z) =>
            z.id === id ? { ...z, status, updatedAt: Date.now() } : z
          ),
        }));
        remotePatchZadeva(id, { status });
      },

      // ---- Panel ----

      setLeftPanelTab: (tab) => {
        set({ leftPanelTab: tab });
      },

      // ---- Messages ----

      addMessage: (msg) => {
        const state = get();
        const activeId = state.activeZadevaId;
        if (!activeId) return;
        const z = state.zadeve.find((zz) => zz.id === activeId);
        if (!z) return;

        // Stabilen ordinal: max obstoječih + 1. Odporno na (a) slicing — slice odreže
        // najstarejše/najnižje, max ostane; (b) regeneracijo — ponovno uporabi isti
        // ordinal; (c) migracijsko sejo — stara lokalna sporočila brez ordinala se
        // efektivno štejejo po array-indeksu (enako, kot jih dodeli /api/migrate-local).
        const maxOrd = z.messages.reduce((m, x, i) => Math.max(m, x.ordinal ?? i), -1);
        const ordinal = maxOrd + 1;
        const stored: Message = { ...msg, ordinal };

        // Auto-title from first user message
        let title = z.title;
        if (title === 'Nov pogovor' && msg.role === 'user' && z.messages.length === 0) {
          title = msg.content.replace(/\[NALO[ŽZ]EN DOKUMENT:[^\]]*\]\n[\s\S]*?\[UPORABNIKOVO VPRA[ŠS]ANJE\]\n/i, '').slice(0, 50).trim() || 'Nov pogovor';
        }
        const titleChanged = title !== z.title;

        set((s) => ({
          zadeve: s.zadeve.map((zz) => {
            if (zz.id !== activeId) return zz;
            let messages = [...zz.messages, stored];
            if (messages.length > MAX_MESSAGES) {
              messages = messages.slice(-MAX_MESSAGES);
            }
            return { ...zz, messages, title, updatedAt: Date.now() };
          }),
        }));

        remotePushMessage(activeId, ordinal, stored);
        if (titleChanged) remotePatchZadeva(activeId, { title });
      },

      updateMessage: (msgId, partial) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return {
                ...z,
                messages: z.messages.map((m) =>
                  m.id === msgId ? { ...m, ...partial } : m
                ),
              };
            }),
          };
        });

        // Push posodobljeno sporočilo (debounced per ordinal → streaming chunki se združijo).
        pushMessageById(get, msgId);
      },

      getActiveMessages: () => {
        const state = get();
        const zadeva = state.zadeve.find((z) => z.id === state.activeZadevaId);
        return zadeva?.messages ?? EMPTY_MESSAGES;
      },

      clearActiveMessages: () => {
        const activeId = get().activeZadevaId;
        set((state) => {
          if (!activeId) return state;
          return {
            zadeve: state.zadeve.map((z) =>
              z.id === activeId
                ? { ...z, messages: [], conversationSummary: undefined, summarizedUpToIndex: undefined, updatedAt: Date.now() }
                : z
            ),
          };
        });
        if (activeId) remoteClearMessages(activeId);
      },

      removeLastAssistantMessage: () => {
        const state = get();
        const activeId = state.activeZadevaId;
        if (!activeId) return null;
        const zadeva = state.zadeve.find((z) => z.id === activeId);
        if (!zadeva) return null;

        // Find last assistant message index
        let lastAiIdx = -1;
        for (let i = zadeva.messages.length - 1; i >= 0; i--) {
          if (zadeva.messages[i].role === 'assistant') {
            lastAiIdx = i;
            break;
          }
        }
        if (lastAiIdx === -1) return null;

        // Find the user message that preceded it
        let userQuery: string | null = null;
        for (let i = lastAiIdx - 1; i >= 0; i--) {
          if (zadeva.messages[i].role === 'user') {
            userQuery = zadeva.messages[i].content;
            break;
          }
        }

        // Remove assistant message (and any trailing messages after it)
        const removedOrdinals = zadeva.messages.slice(lastAiIdx).map((m) => m.ordinal).filter((o): o is number => o != null);
        set((s) => ({
          zadeve: s.zadeve.map((z) =>
            z.id === activeId
              ? { ...z, messages: z.messages.slice(0, lastAiIdx), updatedAt: Date.now() }
              : z
          ),
        }));

        for (const ord of removedOrdinals) remoteDeleteMessage(activeId, ord);
        return userQuery;
      },

      // ---- Dossier (per-zadeva) ----

      addDossierCard: (cardData) => {
        const newCard: DossierCard = {
          id: generateId(),
          ts: Date.now(),
          title: (cardData.title || '').trim().slice(0, 200),
          source: cardData.source || 'generic',
          ref: (cardData.ref || '').trim().slice(0, 200),
          url: (cardData.url || '').trim(),
          content: (cardData.content || '').trim(),
        };

        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              let cards = [...z.dossierCards, newCard];
              if (cards.length > MAX_DOSSIER_CARDS) {
                cards = cards.slice(-MAX_DOSSIER_CARDS);
              }
              return { ...z, dossierCards: cards, updatedAt: Date.now() };
            }),
          };
        });
      },

      removeDossierCard: (cardId) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return {
                ...z,
                dossierCards: z.dossierCards.filter((c) => c.id !== cardId),
                updatedAt: Date.now(),
              };
            }),
          };
        });
      },

      clearDossier: () => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) =>
              z.id === activeId ? { ...z, dossierCards: [], updatedAt: Date.now() } : z
            ),
          };
        });
      },

      getActiveDossierCards: () => {
        const state = get();
        const zadeva = state.zadeve.find((z) => z.id === state.activeZadevaId);
        return zadeva?.dossierCards ?? EMPTY_DOSSIER_CARDS;
      },

      // ---- Documents ----

      addDocument: (docData) => {
        const doc: ZadevaDocument = {
          id: generateId(),
          ...docData,
        };

        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return { ...z, documents: [...z.documents, doc], updatedAt: Date.now() };
            }),
          };
        });
      },

      // ---- Canvas Docs (global — not per-zadeva) ----

      createCanvasDoc: (format: CanvasDocFormat, metadata?: CanvasDocMetadata) => {
        const state = get();
        if (state.canvasDocs.length >= MAX_CANVAS_DOCS) {
          console.warn('[CanvasDoc] Max canvas docs reached:', MAX_CANVAS_DOCS);
          return null;
        }

        const id = generateId();
        const now = Date.now();
        const formatTitles: Record<CanvasDocFormat, string> = {
          mnenje: 'Nomotehnično mnenje SVZ',
          pojasnilo: 'Pravno pojasnilo',
          komentar: 'Komentar k noveli',
          analiza: 'Pravna analiza',
          prosto: 'Dokument',
        };

        const templates: Record<CanvasDocFormat, string> = {
          mnenje: `<h1>Nomotehnično mnenje SVZ</h1>
<p><strong>Predlagatelj:</strong> <em>Ministrstvo za …</em></p>
<p><strong>Predpis:</strong> <em>Naslov predpisa (kratica)</em></p>
<p><strong>EVA:</strong> <em>EVA 2025-XXXX-XXXX</em></p>
<p><strong>Datum:</strong> <em>${new Date().toLocaleDateString('sl-SI')}</em></p>
<h2>1. Identifikacija predpisa</h2>
<p></p>
<h2>2. Pravna podlaga</h2>
<p></p>
<h2>3. Sistemske ugotovitve</h2>
<p></p>
<h2>4. Nomotehnične ugotovitve</h2>
<p></p>
<h2>5. Vsebinske pripombe</h2>
<p></p>
<h2>6. Prehodne in končne določbe</h2>
<p></p>
<h2>7. Sklep</h2>
<p></p>
<h2>8. Uporabljeni viri</h2>
<p></p>`,
          pojasnilo: `<h1>Pravno pojasnilo</h1>
<h2>1. Vprašanje</h2>
<p></p>
<h2>2. Veljavna ureditev</h2>
<p></p>
<h2>3. Pravna analiza</h2>
<p></p>
<h2>4. Zaključek</h2>
<p></p>
<h2>5. Viri</h2>
<p></p>`,
          komentar: `<h1>Komentar k noveli</h1>
<p><strong>Predlagatelj:</strong> <em>Ministrstvo za …</em></p>
<p><strong>Predpis:</strong> <em>Naslov predpisa (kratica)</em></p>
<p><strong>EVA:</strong> <em>EVA 2025-XXXX-XXXX</em></p>
<h2>1. Uvod</h2>
<p></p>
<h2>2. Obseg sprememb</h2>
<p></p>
<h2>3. Pripombe po členih novele</h2>
<p></p>
<h2>4. Prehodne določbe novele</h2>
<p></p>
<h2>5. Splošna ocena</h2>
<p></p>
<h2>6. Viri</h2>
<p></p>`,
          analiza: `<h1>Pravna analiza</h1>
<h2>1. Predmet analize</h2>
<p></p>
<h2>2. Pravni okvir</h2>
<p></p>
<h2>3. Analiza</h2>
<p></p>
<h2>4. Ugotovitve</h2>
<p></p>
<h2>5. Viri</h2>
<p></p>`,
          prosto: '<p></p>',
        };

        const newDoc: CanvasDoc = {
          id,
          title: formatTitles[format],
          format,
          html: templates[format],
          metadata: metadata,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          canvasDocs: [...state.canvasDocs, newDoc],
        }));

        remotePutCanvas({ id, format: newDoc.format, title: newDoc.title, html: newDoc.html, metadata: newDoc.metadata, turns: newDoc.turns });
        return id;
      },

      updateCanvasDoc: (id: string, html: string, title?: string) => {
        if (html.length > MAX_CANVAS_DOC_SIZE) {
          console.warn('[CanvasDoc] Document exceeds max size:', html.length, '/', MAX_CANVAS_DOC_SIZE);
        }

        set((state) => ({
          canvasDocs: state.canvasDocs.map((doc) =>
            doc.id === id
              ? { ...doc, html, ...(title !== undefined ? { title } : {}), updatedAt: Date.now() }
              : doc
          ),
        }));
        pushCanvasById(get, id); // debounced v sync plasti
      },

      deleteCanvasDoc: (id: string) => {
        set((state) => ({
          canvasDocs: state.canvasDocs.filter((doc) => doc.id !== id),
        }));
        remoteDeleteCanvas(id);
        // Izbrisan canvas ne sme ostati v zgodovini pregledovalnika (← →);
        // sicer se pravnik pri kroženju znajde v praznem izbirniku (seja 343)
        useDocumentViewerStore.getState().removeCanvasEntries([id]);
      },
      clearAllCanvasDocs: () => {
        const ids = get().canvasDocs.map((d) => d.id);
        set({ canvasDocs: [] });
        for (const id of ids) remoteDeleteCanvas(id);
        useDocumentViewerStore.getState().removeCanvasEntries(ids);
      },

      getCanvasDoc: (id: string) => {
        return get().canvasDocs.find((doc) => doc.id === id);
      },

      getAllCanvasDocs: () => {
        return get().canvasDocs;
      },

      addCanvasTurn: (docId: string, turn: CanvasTurn) => {
        set((state) => ({
          canvasDocs: state.canvasDocs.map((doc) => {
            if (doc.id !== docId) return doc;
            const turns = [...(doc.turns || []), turn].slice(-20); // max 20
            return { ...doc, turns, updatedAt: Date.now() };
          }),
        }));
        pushCanvasById(get, docId);
      },

      addFragmentToCanvas: (docId: string, html: string, sourceType: string, sourceLabel: string) => {
        const blockId = generateId();
        const blockHtml = `<div data-content-block="true" data-block-id="${blockId}" data-source-type="${sourceType}" data-source-label="${sourceLabel}" data-created="${Date.now()}">${html}</div>`;

        set((state) => ({
          canvasDocs: state.canvasDocs.map((doc) => {
            if (doc.id !== docId) return doc;
            // Append block at end
            const newHtml = doc.html + blockHtml;
            return { ...doc, html: newHtml, updatedAt: Date.now() };
          }),
        }));
        pushCanvasById(get, docId);
      },

      removeDocument: (docId) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return {
                ...z,
                documents: z.documents.filter((d) => d.id !== docId),
                updatedAt: Date.now(),
              };
            }),
          };
        });
      },

      // ---- Filters (per-zadeva) ----

      saveFilters: (filters: SavedFilters, zadevaId?: string) => {
        const targetId = zadevaId ?? get().activeZadevaId;
        set((state) => {
          if (!targetId) return state;

          return {
            zadeve: state.zadeve.map((z) =>
              z.id === targetId ? { ...z, savedFilters: filters } : z
            ),
          };
        });
        if (targetId) remotePatchZadeva(targetId, { savedFilters: filters });
      },

      getSavedFilters: () => {
        const state = get();
        const zadeva = state.zadeve.find((z) => z.id === state.activeZadevaId);
        return zadeva?.savedFilters;
      },

      // ---- Search Context (per-zadeva) ----

      updateSearchContext: (partial: Partial<SearchContext>) => {
        const activeId = get().activeZadevaId;
        let nextContext: SearchContext | undefined;
        set((state) => {
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              nextContext = { ...z.searchContext, ...partial, timestamp: Date.now() };
              return { ...z, searchContext: nextContext };
            }),
          };
        });
        if (activeId && nextContext) remotePatchZadeva(activeId, { searchContext: nextContext });
      },

      getSearchContext: () => {
        const state = get();
        const zadeva = state.zadeve.find((z) => z.id === state.activeZadevaId);
        return zadeva?.searchContext;
      },

      // ---- Rolling destilat pogovora (seja 219, B1) ----

      setConversationSummary: (summary: string, upToIndex: number) => {
        const activeId = get().activeZadevaId;
        set((state) => {
          if (!activeId) return state;
          return {
            zadeve: state.zadeve.map((z) =>
              z.id === activeId ? { ...z, conversationSummary: summary, summarizedUpToIndex: upToIndex } : z
            ),
          };
        });
        if (activeId) remotePatchZadeva(activeId, { conversationSummary: summary, summarizedUpToIndex: upToIndex });
      },

      getConversationSummary: () => {
        const state = get();
        const zadeva = state.zadeve.find((z) => z.id === state.activeZadevaId);
        if (!zadeva?.conversationSummary) return undefined;
        return { summary: zadeva.conversationSummary, upToIndex: zadeva.summarizedUpToIndex ?? 0 };
      },

      // ---- Feedback ----

      setMessageFeedback: (msgId: string, feedback: MessageFeedbackData | undefined) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return {
                ...z,
                messages: z.messages.map((m) =>
                  m.id === msgId ? { ...m, feedback } : m
                ),
              };
            }),
          };
        });
        pushMessageById(get, msgId);
      },

      // ---- Eval (seja 155) ----

      setMessageEval: (msgId: string, evalData: MessageEvalData | undefined) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return {
                ...z,
                messages: z.messages.map((m) =>
                  m.id === msgId ? { ...m, eval: evalData } : m
                ),
              };
            }),
          };
        });
        pushMessageById(get, msgId);
      },

      // ---- SP-LAZY (seja 155) ----

      appendSpCards: (msgId: string, newCards: SearchResultCard[], nextPage: number, hasMore: boolean) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;

          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              return {
                ...z,
                messages: z.messages.map((m) => {
                  if (m.id !== msgId) return m;
                  // Dedup po card.id (lahko se zgodi pri ponovnem kliku)
                  const existingIds = new Set([
                    ...(m.resultCards || []).map((c) => c.id),
                    ...(m.additionalSpCards || []).map((c) => c.id),
                  ]);
                  const dedupedNew = newCards.filter((c) => !existingIds.has(c.id));
                  return {
                    ...m,
                    additionalSpCards: [...(m.additionalSpCards || []), ...dedupedNew],
                    spLazyPage: nextPage,
                    spHasMore: hasMore,
                  };
                }),
              };
            }),
          };
        });
        pushMessageById(get, msgId);
      },

      // ---- Nomotehnika follow-up ----

      saveNomotehniskaReview: (review) => {
        const activeId = get().activeZadevaId;
        set((state) => {
          if (!activeId) return state;
          return {
            zadeve: state.zadeve.map((z) =>
              z.id === activeId
                ? { ...z, latestNomotehniskaReview: review, updatedAt: Date.now() }
                : z
            ),
          };
        });
        // Persistiraj le končni save (ne sprotnih partial update-ov med tek-om).
        if (activeId) remotePatchZadeva(activeId, { latestNomotehniskaReview: review });
      },

      // Seja 177: partial merge — uporabljeno za sproti shranjevanje med tek-om,
      // brez prepisovanja celotnega review-ja vsakič (analysisText raste sproti).
      updateNomotehniskaReview: (patch) => {
        set((state) => {
          const activeId = state.activeZadevaId;
          if (!activeId) return state;
          return {
            zadeve: state.zadeve.map((z) => {
              if (z.id !== activeId) return z;
              const current = z.latestNomotehniskaReview;
              if (!current) {
                // Ne moremo merge-ati v nič — patch mora vsebovati vsa obvezna polja.
                if (!patch.documentName || patch.analysisText === undefined || !patch.timestamp) {
                  return z;
                }
                return {
                  ...z,
                  latestNomotehniskaReview: patch as NomotehniskaReviewResult,
                  updatedAt: Date.now(),
                };
              }
              return {
                ...z,
                latestNomotehniskaReview: { ...current, ...patch },
                updatedAt: Date.now(),
              };
            }),
          };
        });
      },

      clearNomotehniskaReview: () => {
        const activeId = get().activeZadevaId;
        set((state) => {
          if (!activeId) return state;
          return {
            zadeve: state.zadeve.map((z) =>
              z.id === activeId
                ? { ...z, latestNomotehniskaReview: undefined, updatedAt: Date.now() }
                : z
            ),
          };
        });
        if (activeId) remotePatchZadeva(activeId, { latestNomotehniskaReview: null });
      },

      getLatestNomotehniskaReview: () => {
        const state = get();
        const zadeva = state.zadeve.find((z) => z.id === state.activeZadevaId);
        return zadeva?.latestNomotehniskaReview;
      },

      // ---- Migration ----
      setMigrated: () => {
        set({ _migrated: true });
      },

      // ---- Server sync hidracija (seja 219, A4) ----

      _applyServerZadeveList: (list) => {
        set((state) => {
          const byId = new Map(state.zadeve.map((z) => [z.id, z] as const));
          for (const s of list) {
            const createdAt = toEpochMs(s.createdAt);
            const updatedAt = toEpochMs(s.updatedAt);
            const local = byId.get(s.id);
            if (local) {
              byId.set(s.id, { ...local, title: s.title, status: s.status as Zadeva['status'], updatedAt });
            } else {
              // metadata-only placeholder; messages se naložijo lazy ob odprtju
              byId.set(s.id, {
                id: s.id, title: s.title, status: s.status as Zadeva['status'],
                createdAt, updatedAt,
                messages: [], dossierCards: [], documents: [], canvasDocs: [],
              });
            }
          }
          const zadeve = Array.from(byId.values()).sort((a, b) => toEpochMs(b.updatedAt) - toEpochMs(a.updatedAt));
          return { zadeve };
        });
      },

      _applyServerZadevaDetail: (id, detail) => {
        set((state) => ({
          zadeve: state.zadeve.map((z) => {
            if (z.id !== id) return z;
            const d = detail as Partial<Zadeva> & { messages?: Message[]; documents?: ZadevaDocument[]; dossierCards?: DossierCard[] };
            return {
              ...z,
              title: d.title ?? z.title,
              status: (d.status as Zadeva['status']) ?? z.status,
              updatedAt: d.updatedAt ?? z.updatedAt,
              messages: Array.isArray(d.messages) ? d.messages : z.messages,
              documents: Array.isArray(d.documents) ? d.documents : z.documents,
              dossierCards: Array.isArray(d.dossierCards) ? d.dossierCards : z.dossierCards,
              conversationSummary: d.conversationSummary ?? z.conversationSummary,
              summarizedUpToIndex: d.summarizedUpToIndex ?? z.summarizedUpToIndex,
              savedFilters: d.savedFilters ?? z.savedFilters,
              searchContext: d.searchContext ?? z.searchContext,
              latestNomotehniskaReview: d.latestNomotehniskaReview ?? z.latestNomotehniskaReview,
            };
          }),
        }));
      },

      _applyServerCanvas: (docs) => {
        set((state) => {
          const byId = new Map(state.canvasDocs.map((c) => [c.id, c] as const));
          for (const d of docs) byId.set(d.id, d); // server zmaga za isti ID
          const canvasDocs = Array.from(byId.values()).sort((a, b) => b.updatedAt - a.updatedAt);
          return { canvasDocs };
        });
      },
    }),
    {
      name: 'tribonianus-zadeve',
      version: 1,
      storage: createJSONStorage(() => quotaSafeStorage),
      partialize: (state) => ({
        zadeve: state.zadeve,
        activeZadevaId: state.activeZadevaId,
        leftPanelTab: state.leftPanelTab,
        canvasDocs: state.canvasDocs,
      }),
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;
        if (version === 0) {
          // Migrate per-zadeva canvasDocs to global canvasDocs
          const zadeve = (state.zadeve || []) as Array<Record<string, unknown>>;
          const globalDocs: CanvasDoc[] = [];
          const seenIds = new Set<string>();

          for (const z of zadeve) {
            const docs = (z.canvasDocs || []) as CanvasDoc[];
            for (const doc of docs) {
              if (!seenIds.has(doc.id)) {
                seenIds.add(doc.id);
                globalDocs.push(doc);
              }
            }
            // Clear per-zadeva canvas docs after migration
            z.canvasDocs = [];
          }

          return { ...state, zadeve, canvasDocs: globalDocs };
        }
        return state;
      },
    }
  )
);
